import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Brain,
  Search,
  CheckCircle2,
  Lightbulb,
  Target,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  Zap,
  Shield,
  BarChart3,
  PieChart,
  Activity,
  Award,
  ArrowRight,
  RefreshCw,
  Download,
  Share2,
  Sparkles,
  Cpu,
  Database,
  Globe,
  Users,
  DollarSign,
  Calendar,
  FileText,
  CheckSquare,
  XCircle,
  AlertCircle,
  Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Animated Score Circle Component
const ScoreCircle = ({ score, size = 180, strokeWidth = 12, label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    if (score >= 40) return '#F97316';
    return '#EF4444';
  };

  return (
    <div className="relative flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getColor(score)}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-4xl font-bold text-gray-800">{score?.toFixed(1)}</span>
        <span className="text-sm text-gray-500">{label || 'Score'}</span>
      </div>
    </div>
  );
};

// Agent Status Badge
const MODEL_BRAND = {
  'gpt-6-astra': 'GPT-6 Astra',
  'claude-fable-5-1': 'Claude Fable 5.1',
  'gemini-3.8-flash': 'Gemini 3.8 Flash',
};

const AgentBadge = ({ agent, model, status }) => {
  const agentIcons = {
    orchestrator: <Cpu className="w-4 h-4" />,
    analysis: <Brain className="w-4 h-4" />,
    research: <Search className="w-4 h-4" />,
    validation: <Shield className="w-4 h-4" />,
    recommendation: <Lightbulb className="w-4 h-4" />
  };

  const agentColors = {
    orchestrator: 'bg-purple-100 text-purple-700 border-purple-200',
    analysis: 'bg-blue-100 text-blue-700 border-blue-200',
    research: 'bg-green-100 text-green-700 border-green-200',
    validation: 'bg-amber-100 text-amber-700 border-amber-200',
    recommendation: 'bg-rose-100 text-rose-700 border-rose-200'
  };

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${agentColors[agent] || 'bg-gray-100'}`}>
      {agentIcons[agent]}
      <div className="flex flex-col">
        <span className="text-xs font-semibold capitalize">{agent}</span>
        <span className="text-[10px] opacity-75">{MODEL_BRAND[model] || model}</span>
      </div>
      {status === 'completed' && <CheckCircle2 className="w-3 h-3 ml-auto text-green-600" />}
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Icon className="w-5 h-5 text-emerald-600" />
          </div>
          <span className="font-semibold text-gray-800">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && <div className="p-4 pt-0 border-t border-gray-100">{children}</div>}
    </div>
  );
};

// Horizontal Bar Chart Component
const HorizontalBarChart = ({ data, maxValue = 100 }) => {
  return (
    <div className="space-y-3">
      {data.map((item, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{item.label}</span>
            <span className="font-semibold text-gray-800">{item.value}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-500"
              style={{ width: `${(item.value / maxValue) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Quick Win Card
const QuickWinCard = ({ action, timeline, impact, index }) => {
  return (
    <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
          {index + 1}
        </div>
        <div className="flex-1">
          <p className="text-gray-800 font-medium text-sm">{action}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeline}
            </span>
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {impact}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Strategic Priority Card
const PriorityCard = ({ priority, initiative, description, impact, timeline, roi, index }) => {
  const impactColors = {
    'High': 'bg-red-100 text-red-700',
    'Medium': 'bg-amber-100 text-amber-700',
    'Low': 'bg-green-100 text-green-700'
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-emerald-600 text-white text-xs font-bold rounded">
            P{priority || index + 1}
          </span>
          <h4 className="font-semibold text-gray-800">{initiative}</h4>
        </div>
        <span className={`px-2 py-0.5 text-xs rounded-full ${impactColors[impact] || 'bg-gray-100'}`}>
          {impact} Impact
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-3">{description}</p>
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {timeline}
        </span>
        {roi && (
          <span className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            ROI: {roi}
          </span>
        )}
      </div>
    </div>
  );
};

// Validation Status Component
const ValidationStatus = ({ validation }) => {
  const statusConfig = {
    'PASSED': { icon: CheckCircle2, color: 'text-green-600 bg-green-50', label: 'Validated' },
    'PASSED_WITH_WARNINGS': { icon: AlertCircle, color: 'text-amber-600 bg-amber-50', label: 'Passed with Warnings' },
    'FAILED': { icon: XCircle, color: 'text-red-600 bg-red-50', label: 'Failed Validation' }
  };

  const status = validation?.validation_status || 'PASSED';
  const config = statusConfig[status] || statusConfig['PASSED'];
  const Icon = config.icon;

  return (
    <div className={`p-4 rounded-lg ${config.color}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" />
        <span className="font-semibold">{config.label}</span>
        <span className="ml-auto text-sm">
          Confidence: {((validation?.overall_confidence || 0.8) * 100).toFixed(0)}%
        </span>
      </div>
      
      {validation?.data_quality && (
        <div className="grid grid-cols-4 gap-2 mt-3">
          {Object.entries(validation.data_quality).map(([key, value]) => (
            <div key={key} className="text-center p-2 bg-white/50 rounded">
              <div className="text-lg font-bold">{((value || 0) * 100).toFixed(0)}%</div>
              <div className="text-xs capitalize">{key}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
const MultiAgentDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const assessmentId = searchParams.get('id');
  
  const [assessment, setAssessment] = useState(null);
  const [pastAssessments, setPastAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);

  useEffect(() => {
    if (assessmentId) {
      fetchAssessment();
    } else {
      fetchPastAssessments();
    }
    fetchSystemStatus();
  }, [assessmentId]);

  const fetchAssessment = async () => {
    if (!assessmentId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/multi-agent/assessments/${assessmentId}`);
      setAssessment(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch assessment:', err);
      setError('Assessment not found or still processing');
    } finally {
      setLoading(false);
    }
  };

  const fetchPastAssessments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/multi-agent/assessments`);
      setPastAssessments(response.data || []);
    } catch (err) {
      console.error('Failed to fetch past assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/multi-agent/status`);
      setSystemStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch system status:', err);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-emerald-200 rounded-full animate-pulse" />
            <div className="absolute inset-2 border-4 border-t-emerald-600 rounded-full animate-spin" />
            <Brain className="absolute inset-0 m-auto w-8 h-8 text-emerald-600" />
          </div>
          <p className="text-gray-600">Loading Multi-Agent Assessment...</p>
        </div>
      </div>
    );
  }

  // No Assessment Selected State
  if (!assessmentId || !assessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Multi-Agent Intelligence System
            </div>
            <h1 data-testid="dashboard-title" className="text-3xl font-bold text-gray-800 mb-2">AGI-Powered Assessment Dashboard</h1>
            <p className="text-gray-600">Comprehensive enterprise assessments using 5 specialized AI agents</p>
          </div>

          {/* System Status */}
          {systemStatus && (
            <div data-testid="system-status" className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">System Status</h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  systemStatus.status === 'operational' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {systemStatus.status === 'operational' ? 'Operational' : 'Offline'}
                </span>
              </div>
              
              <div className="grid grid-cols-5 gap-3">
                {Object.entries(systemStatus.agents || {}).map(([agent, config]) => (
                  <AgentBadge
                    key={agent}
                    agent={agent}
                    model={config.model}
                    status={config.status}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Assessments List */}
          {pastAssessments.length > 0 && (
            <div data-testid="past-assessments" className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Past Assessments
              </h2>
              <div className="space-y-3">
                {pastAssessments.map((pa) => (
                  <button
                    key={pa.id}
                    data-testid={`assessment-item-${pa.id}`}
                    onClick={() => navigate(`/multi-agent-dashboard?id=${pa.id}`)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-emerald-50 hover:border-emerald-200 border border-gray-100 transition-all text-left"
                  >
                    <div>
                      <div className="font-medium text-gray-800">{pa.company_name}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
                        <span>{pa.industry}</span>
                        <span>{pa.assessment_type}</span>
                        <span>{new Date(pa.created_at || pa.timestamp).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        pa.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {pa.status || 'completed'}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Run New Assessment CTA */}
          <div data-testid="new-assessment-cta" className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-8 text-white text-center">
            <Brain className="w-16 h-16 mx-auto mb-4 opacity-90" />
            <h3 className="text-2xl font-bold mb-2">Run a Multi-Agent Assessment</h3>
            <p className="opacity-90 mb-6">
              Get comprehensive analysis from 5 specialized AI agents working together
            </p>
            <button
              data-testid="start-assessment-btn"
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
            >
              Start New Assessment
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertTriangle className="w-5 h-5 inline mr-2" />
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  const { analysis, research, validation, recommendations, final_assessment } = assessment;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-2 text-emerald-100 text-sm mb-2">
            <Sparkles className="w-4 h-4" />
            Multi-Agent Intelligence Assessment
          </div>
          <h1 className="text-3xl font-bold mb-2">{assessment.company_name}</h1>
          <div className="flex items-center gap-4 text-emerald-100">
            <span className="flex items-center gap-1">
              <Globe className="w-4 h-4" />
              {assessment.industry}
            </span>
            <span className="flex items-center gap-1">
              <BarChart3 className="w-4 h-4" />
              {assessment.assessment_type}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(assessment.timestamp || assessment.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6">
        {/* Score Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Main Score Card */}
          <div className="md:col-span-1 bg-white rounded-xl shadow-lg p-6 flex flex-col items-center">
            <ScoreCircle 
              score={analysis?.overall_score || final_assessment?.final_maturity_score || 0} 
              label="Overall Score"
            />
            <div className="mt-4 text-center">
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full font-semibold">
                {analysis?.maturity_level || final_assessment?.maturity_level || 'N/A'}
              </span>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
              <Shield className="w-4 h-4" />
              Confidence: {((assessment.confidence_score || 0) * 100).toFixed(0)}%
            </div>
          </div>

          {/* Agents Used */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-emerald-600" />
              AI Agents Deployed
            </h3>
            <div className="space-y-2">
              {(assessment.agents_used || ['orchestrator', 'analysis', 'research', 'validation', 'recommendation']).map(agent => {
                const modelConfig = assessment.model_configuration?.[agent] || ['openai', 'gpt-6-astra'];
                return (
                  <AgentBadge
                    key={agent}
                    agent={agent}
                    model={Array.isArray(modelConfig) ? modelConfig[1] : modelConfig}
                    status="completed"
                  />
                );
              })}
            </div>
          </div>

          {/* Key Metrics */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              Key Metrics
            </h3>
            {analysis?.benchmark_comparison && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">vs Industry Avg</span>
                  <span className={`font-bold ${
                    (analysis.benchmark_comparison.vs_industry_avg || 0) >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {analysis.benchmark_comparison.vs_industry_avg >= 0 ? '+' : ''}
                    {analysis.benchmark_comparison.vs_industry_avg}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Percentile Rank</span>
                  <span className="font-bold text-gray-800">
                    {analysis.benchmark_comparison.percentile_rank}th
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Quick Wins</span>
                  <span className="font-bold text-amber-600">
                    {recommendations?.quick_wins?.length || 0} identified
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Priorities</span>
                  <span className="font-bold text-emerald-600">
                    {recommendations?.strategic_priorities?.length || 0} defined
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Analysis Section */}
          <CollapsibleSection title="Deep Analysis" icon={Brain} badge="GPT-6 Astra">
            <div className="space-y-4 mt-4">
              {/* Dimension Scores */}
              {analysis?.dimension_scores && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Dimension Scores</h4>
                  <HorizontalBarChart
                    data={Object.entries(analysis.dimension_scores).map(([key, val]) => ({
                      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                      value: val?.score || val || 0
                    }))}
                  />
                </div>
              )}

              {/* Strengths */}
              {analysis?.strengths && analysis.strengths.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Strengths
                  </h4>
                  <ul className="space-y-1">
                    {analysis.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-green-500 mt-1">•</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {analysis?.weaknesses && analysis.weaknesses.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Areas for Improvement
                  </h4>
                  <ul className="space-y-1">
                    {analysis.weaknesses.map((w, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Research Section */}
          <CollapsibleSection title="Industry Research" icon={Search} badge="Gemini 3.8 Flash">
            <div className="space-y-4 mt-4">
              {/* Benchmarks */}
              {research?.industry_benchmarks && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">Industry Benchmarks</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {research.industry_benchmarks.average_maturity_score}
                      </div>
                      <div className="text-xs text-blue-600">Industry Avg</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-600">
                        {research.industry_benchmarks.top_quartile_threshold}
                      </div>
                      <div className="text-xs text-emerald-600">Top Quartile</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {research.industry_benchmarks.leader_threshold}
                      </div>
                      <div className="text-xs text-purple-600">Leader</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Best Practices */}
              {research?.best_practices && research.best_practices.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Best Practices</h4>
                  <div className="space-y-2">
                    {research.best_practices.slice(0, 3).map((bp, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="font-medium text-gray-800">{bp.practice}</div>
                        <div className="text-gray-500 text-xs mt-1">{bp.description}</div>
                        <div className="flex gap-2 mt-2">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                            {bp.adoption_rate} adoption
                          </span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                            {bp.impact} impact
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Emerging Trends */}
              {research?.emerging_trends && research.emerging_trends.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Emerging Trends</h4>
                  <div className="flex flex-wrap gap-2">
                    {research.emerging_trends.map((trend, i) => (
                      <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {trend.trend || trend}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Validation Section */}
          <CollapsibleSection title="Quality Validation" icon={Shield} badge="Claude Fable 5.1">
            <div className="mt-4">
              <ValidationStatus validation={validation} />
              
              {validation?.anomalies_detected && validation.anomalies_detected.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Anomalies Detected</h4>
                  {validation.anomalies_detected.map((anomaly, i) => (
                    <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">{anomaly.anomaly}</span>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">{anomaly.recommendation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Investment Summary */}
          {recommendations?.investment_summary && (
            <CollapsibleSection title="Investment Summary" icon={DollarSign} badge="ROI Analysis">
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-600">
                    {recommendations.investment_summary.total_estimated_investment}
                  </div>
                  <div className="text-xs text-emerald-700">Total Investment</div>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {recommendations.investment_summary.payback_period}
                  </div>
                  <div className="text-xs text-blue-700">Payback Period</div>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg text-center col-span-2">
                  <div className="text-2xl font-bold text-purple-600">
                    {recommendations.investment_summary.risk_adjusted_roi}
                  </div>
                  <div className="text-xs text-purple-700">Risk-Adjusted ROI</div>
                </div>
              </div>
            </CollapsibleSection>
          )}
        </div>

        {/* Quick Wins Section */}
        {recommendations?.quick_wins && recommendations.quick_wins.length > 0 && (
          <div className="mt-6">
            <CollapsibleSection title="Quick Wins" icon={Zap} badge={`${recommendations.quick_wins.length} Actions`}>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendations.quick_wins.map((qw, i) => (
                  <QuickWinCard
                    key={i}
                    index={i}
                    action={qw.action || qw}
                    timeline={qw.timeline || '1-3 months'}
                    impact={qw.impact || 'High'}
                  />
                ))}
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* Strategic Priorities Section */}
        {recommendations?.strategic_priorities && recommendations.strategic_priorities.length > 0 && (
          <div className="mt-6">
            <CollapsibleSection title="Strategic Priorities" icon={Target} badge={`${recommendations.strategic_priorities.length} Initiatives`}>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendations.strategic_priorities.map((sp, i) => (
                  <PriorityCard
                    key={i}
                    index={i}
                    priority={sp.priority}
                    initiative={sp.initiative}
                    description={sp.description}
                    impact={sp.business_impact}
                    timeline={sp.timeline}
                    roi={sp.expected_roi}
                  />
                ))}
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* Transformation Roadmap */}
        {recommendations?.transformation_roadmap && (
          <div className="mt-6">
            <CollapsibleSection title="Transformation Roadmap" icon={Calendar} badge="3 Phases">
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(recommendations.transformation_roadmap).map(([phase, data], i) => (
                  <div key={phase} className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        i === 0 ? 'bg-blue-500' : i === 1 ? 'bg-emerald-500' : 'bg-purple-500'
                      }`}>
                        {i + 1}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800">{data.name}</div>
                        <div className="text-xs text-gray-500">{data.duration}</div>
                      </div>
                    </div>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {data.objectives?.slice(0, 3).map((obj, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckSquare className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {obj}
                        </li>
                      ))}
                    </ul>
                    {data.investment && (
                      <div className="mt-3 pt-3 border-t border-gray-100 text-sm font-medium text-emerald-600">
                        Investment: {data.investment}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* Final Synthesis */}
        {final_assessment && (
          <div className="mt-6">
            <CollapsibleSection title="Executive Summary" icon={FileText} badge="Final Synthesis">
              <div className="mt-4 space-y-4">
                {final_assessment.key_insights && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Insights</h4>
                    <ul className="space-y-2">
                      {final_assessment.key_insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {final_assessment.top_priorities && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Priorities</h4>
                    <ul className="space-y-2">
                      {final_assessment.top_priorities.map((priority, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Target className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {priority}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {final_assessment.next_immediate_action && (
                  <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg text-white">
                    <h4 className="font-semibold mb-1 flex items-center gap-2">
                      <ArrowRight className="w-5 h-5" />
                      Next Immediate Action
                    </h4>
                    <p className="text-emerald-50">{final_assessment.next_immediate_action}</p>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center gap-4">
          <button
            onClick={() => window.print()}
            className="px-6 py-3 bg-white border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export Report
          </button>
          <button
            onClick={fetchAssessment}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh Data
          </button>
        </div>

        {/* Processing Log (for debugging) */}
        {assessment.processing_log && assessment.processing_log.length > 0 && (
          <div className="mt-8">
            <CollapsibleSection title="Processing Log" icon={Activity} defaultOpen={false}>
              <div className="mt-4 bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto">
                {assessment.processing_log.map((log, i) => (
                  <div key={i} className="text-xs text-gray-300 font-mono mb-1">
                    {log}
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiAgentDashboard;
